import { createElement } from 'react';

import type { Classnames } from '@snowit/icetypes';

import useA11y from '@snowit/hooks/useA11y';
import { Icon, Markdown, Typo } from '@snowit/ui';
import Img from '@snowit/ui/Img';

import cx from 'classnames';
import s from './BannerMarketing.module.scss';

export interface IBannerMarketing {
  image?: string;
  title?: string;
  text?: string;
  href?: string;
  colorFrom?: string;
  colorTo?: string;
  colorText?: string;
  onClick?: () => boolean; // Must return a Boolean to allow <a> follow link, if true follow link
  classNames?: Classnames<'wrapper' | 'banner' | 'title' | 'text'>;
}

const BannerMarketing = (props: IBannerMarketing) => {
  const {
    image,
    title,
    text,
    href,
    colorFrom = 'var(--c-primary-d)',
    colorTo = 'var(--c-primary)',
    colorText = 'var(--c-text-inverse)',
    onClick,
    classNames,
  } = props;
  const a11yH = useA11y();

  const titleId = a11yH.makeId('title');

  const tag = href ? 'a' : 'div';

  const target = String(href).match(/^(http|mailto:|tel:)/) ? '_blank' : '_self';

  const renderImage = () => {
    if (!image) return null;
    return (
      <Img
        classNames={{ figure: s.img }}
        src={image}
        alt={title || text || ''}
        ratio={1}
        a11y={{ role: 'presentation' }}
      />
    );
  };

  const renderTitle = () => {
    if (!title) return null;
    return (
      <Typo
        className={classNames?.title}
        text={title}
        tag={'h3'}
        size={['xxl', 'db']}
        weight={'medium'}
        color={'text'}
        balancer
      />
    );
  };

  const renderText = () => {
    if (!text) return null;
    return (
      <Markdown classNames={{ text: classNames?.text }} source={text} baseSize={['md', 'df']} />
    );
  };

  const renderIcon = () => {
    if (!href) return null;
    return <Icon className={s.icon} name={'arrow_angle_right'} a11y={{ role: 'presentation' }} />;
  };

  const BannerContent = (
    <section className={cx(s.banner, classNames?.banner)} aria-labelledby={titleId}>
      {renderImage()}
      <div className={s.body}>
        {renderTitle()}
        {renderText()}
      </div>
      {renderIcon()}
    </section>
  );

  const bannerProps = {
    className: cx(s.wrapper, classNames?.wrapper, {
      [s.link]: href,
    }),
    style: {
      '--color-1': colorFrom,
      '--color-2': colorTo,
      '--lc-text': colorText,
    },
    ...(href && {
      rel: 'noreferrer',
      target: target,
      href: href,
      onClick: onClick,
    }),
  };

  return createElement(tag, bannerProps, BannerContent);
};

export default BannerMarketing;
